import React from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { Typography, Box, Divider, Grid } from '@mui/material';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import { getForcedMessages } from '../../../../../i18n';
import Information from '../../../Components/Information';
import capitalize from '../../../../../Helpers/StringsFormatting/capitalize';
import { culture, options } from '../../../../../Helpers/dates';

const useStyles = () => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
  },
  infoStyle: {
    root: {
      flex: 'unset',
    },
    valueStyle: {
      fontWeight: 500,
    },
    captionStyle: {
      fontWeight: 400,
    },
  },
  boolStyle: {
    fontSize: '0.95rem',
    letterSpacing: '0.02em',
    color: 'inherit',
  },
});

const normalizeNumberValue = (value) => {
  const rawValue = `${value ?? ''}`.trim();

  if (!rawValue) return Number.NaN;

  if (rawValue.includes(',')) {
    return Number.parseFloat(rawValue.replace(/\./g, '').replace(',', '.'));
  }

  return Number.parseFloat(rawValue);
};

const formatNumberValue = (value, { prefix = '€ ', suffix = '' } = {}) => {
  const numericValue = normalizeNumberValue(value);

  if (Number.isNaN(numericValue)) {
    return `${prefix}0,00${suffix}`;
  }

  // Manual formatting to ensure '.' for thousands and ',' for decimals
  const fixed = numericValue.toFixed(2); // ensures two decimals
  const [intPart, fracPart] = fixed.split('.');
  const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${prefix}${withThousands},${fracPart}${suffix}`;
};

const PracticeDetailsPage = function PracticeDetailsPage({
  formData: practiceData = {}, //useForm processes data, if data is inserted gets the inserted otherwise the practice data from backend processed by the hook
}) {
  const intl = useIntl();
  const italianTerms = getForcedMessages('it');
  const classes = useStyles();

  const formatInfo = (info) => {
    //if find a key in intl files get the translated value
    const itKeyName = Object.keys(italianTerms).find(
      (keyName) => italianTerms[keyName].toUpperCase() === info?.toUpperCase()
    );
    if (itKeyName) {
      return intl.messages[itKeyName];
    }
    //else it returns the capitalized value
    return capitalize(info);
  };

  return (
    <div style={classes.root}>
      <Grid container rowSpacing="16px" columnSpacing="18px">
        <Grid size={{ xs: 12 }}>
          <Typography variant="h6">{intl.messages.SUMMARY}</Typography>
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.CUSTOMER}
            value={`${formatInfo(practiceData.customerName)} ${formatInfo(
              practiceData.customerSurname
            )}`}
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.FISCALCODE}
            value={practiceData.fiscalCode}
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.GENDER}
            value={
              practiceData.gender === 'M'
                ? intl.messages.MAN
                : intl.messages.WOMAN
            }
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.AGE}
            value={practiceData.age}
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.NATIONALITY}
            value={formatInfo(practiceData.citizen)}
          />
        </Grid>

        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.OCCUPATION}
            value={formatInfo(practiceData.occupation)}
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.COMPANY}
            value={formatInfo(practiceData.company)}
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.CITY}
            value={formatInfo(practiceData.city)}
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.ZIPCODE}
            value={practiceData.zipCode}
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.PROVINCE}
            value={practiceData.province}
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 6 }} />
        <Grid
          size={{ md: 3, sm: 4, xs: 6 }}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          {(practiceData?.company1 ||
            practiceData?.company2 ||
            practiceData?.company3) && (
            <Box
              sx={{
                display: "flex",
                gap: "4px",
                color: "#6a6a6a",
                overflow: "hidden"
              }}>
              <DoneRoundedIcon sx={{ color: 'inherit' }} />
              <Typography sx={classes.boolStyle} noWrap>
                {intl.messages.ELIGIBLE}
              </Typography>
            </Box>
          )}
        </Grid>
        {(practiceData?.company1 ||
          practiceData?.company2 ||
          practiceData?.company3) && (
          <>
            <Grid size={{ md: 3, sm: 4, xs: 6 }}>
              <Information
                styles={classes.infoStyle}
                caption={intl.messages.COMPANY1}
                value={
                  practiceData?.company1 &&
                  `${practiceData?.company1} - ${practiceData?.rating1}`
                }
              />
            </Grid>
            <Grid size={{ md: 3, sm: 4, xs: 6 }}>
              <Information
                styles={classes.infoStyle}
                caption={intl.messages.COMPANY2}
                value={
                  practiceData?.company2 &&
                  `${practiceData?.company2} - ${practiceData?.rating2}`
                }
              />
            </Grid>
            <Grid size={{ md: 3, sm: 4, xs: 6 }}>
              <Information
                styles={classes.infoStyle}
                caption={intl.messages.COMPANY3}
                value={
                  practiceData?.company3 &&
                  `${practiceData?.company3} - ${practiceData?.rating3}`
                }
              />
            </Grid>
          </>
        )}

        <Grid size={{ xs: 12 }}>
          <Divider />
        </Grid>

        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.PRACTICENUMBER}
            value={practiceData.practiceNumber}
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.DATELOADINGPRACTICE}
            value={
              practiceData.dateLoadingPractice
                ? new Date(practiceData.dateLoadingPractice).toLocaleDateString(
                    culture,
                    options
                  )
                : intl.messages.UNAVAILABLE
            }
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 12 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.PRATICALMANAGEMENT}
            value={formatInfo(practiceData.praticalManagement)}
          />
        </Grid>

        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.LEADSOURCE}
            value={formatInfo(practiceData.leadSource)}
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.LOANPURPOSE}
            value={formatInfo(practiceData.loanPurpose)}
          />
        </Grid>

        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.PRODUCTSOURCE}
            value={formatInfo(practiceData.productSource)}
          />
        </Grid>

        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.PRODUCTCODE}
            value={formatInfo(practiceData.productCode)}
          />
        </Grid>

        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.PRACTICESTATE}
            value={formatInfo(practiceData.practiceState)}
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.LIQUIDATIONDATE}
            value={
              practiceData.liquidationDate
                ? new Date(practiceData.liquidationDate).toLocaleDateString(
                    culture,
                    options
                  )
                : intl.messages.UNAVAILABLE
            }
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Divider />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.DURATION}
            value={
              practiceData.duration?.toString() || intl.messages.UNAVAILABLE
            }
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.REQUIREDAMOUNT}
            value={formatNumberValue(practiceData?.requiredAmount)}
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.NETAMOUNT}
            value={formatNumberValue(practiceData?.netAmount)}
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.STAMP}
            value={formatNumberValue(practiceData?.stamp)}
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.RATE}
            value={formatNumberValue(practiceData?.rate, {
              prefix: '',
              suffix: ' %',
            })}
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.ARRANGEMENTFEES}
            value={formatNumberValue(practiceData?.arrangementFees)}
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.EXTERNALDEBTCONSOLIDATION}
            value={formatNumberValue(practiceData?.externalDebtConsolidation)}
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.INTERNALDEBTCONSOLIDATION}
            value={formatNumberValue(practiceData?.internalDebtConsolidation)}
          />
        </Grid>

        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.INSURANCEPREMIUMAMOUNTBUNDLE}
            value={formatNumberValue(
              practiceData?.insurancePremiumAmountBundle
            )}
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.INSURANCEPREMIUMAMOUNTCPI}
            value={formatNumberValue(practiceData?.insurancePremiumAmountCPI)}
          />
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 6 }}>
          <Information
            styles={classes.infoStyle}
            caption={intl.messages.IDENTINET}
            value={formatNumberValue(practiceData?.identinet)}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Information
            caption={intl.messages.NOTES}
            value={practiceData.notes}
            noWrap={false}
          />
        </Grid>
      </Grid>
    </div>
  );
};

PracticeDetailsPage.propTypes = {
  formData: PropTypes.object,
};

export default PracticeDetailsPage;
